/**
 * WebCrypto sha256 for verifying fetched artifacts against their manifest
 * digest (requirement 2: "verify sha256 of fetched artifacts with WebCrypto
 * and refuse mismatches"). Content addressing is the observatory's own
 * integrity model (`organism.data.ArtifactStore`); this is the browser-side
 * half of the same guarantee.
 */

export class ArtifactIntegrityError extends Error {
  constructor(
    public readonly expected: string,
    public readonly actual: string,
    public readonly url: string,
  ) {
    super(
      `artifact at ${url} failed sha256 verification: expected ${expected}, got ${actual} — refusing to use it`,
    );
    this.name = "ArtifactIntegrityError";
  }
}

/** Lowercase hex sha256 of `data`, computed via `crypto.subtle`. */
export async function sha256Hex(data: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", data);
  const bytes = new Uint8Array(digest);
  let hex = "";
  for (const byte of bytes) {
    hex += byte.toString(16).padStart(2, "0");
  }
  return hex;
}

/** Verifies `data` matches `expectedSha256`; throws `ArtifactIntegrityError` on mismatch. */
export async function verifySha256(
  data: ArrayBuffer,
  expectedSha256: string,
  url: string,
): Promise<void> {
  const actual = await sha256Hex(data);
  if (actual !== expectedSha256.toLowerCase()) {
    throw new ArtifactIntegrityError(expectedSha256.toLowerCase(), actual, url);
  }
}
